// @ts-check
import { createSelector } from '../utils/selector-utils'
import { selectAirwayEvents, selectAirwayTimesByEventId } from './airway'
import { selectAirwayEnabled, selectVitalsTrackingEnabled } from './case-props'
import { selectCustomEventsAsArray } from './custom-events'
import { eventSelectors } from './events'
import { selectFluidEvents, selectFluidTimesByEventId } from './fluids'
import { selectManualVitalsEvents } from './manual-vitals'
import {
  selectPositionEvents,
  selectPositionTimesByEventId,
  selectPositionsSkipped,
} from './position'
import { selectSedativesBeingUsed } from './sedation-level'

/** @type {CaseSelector<CaseEventBase[]>} */
const selectBaseEvents = createSelector(
  selectSedativesBeingUsed,
  selectPositionsSkipped,
  selectAirwayEnabled,
  selectVitalsTrackingEnabled,
  selectPositionEvents,
  selectAirwayEvents,
  selectFluidEvents,
  /**
   * @param {boolean} sedativesBeingUsed
   * @param {boolean} positionsSkipped
   * @param {boolean} airwayEnabled
   * @param {boolean} vitalsTrackingEnabled
   * @param {PositionEvent[]} positionEvents
   * @param {AirwayEvent[]} airwayEvents
   * @param {FluidEvent[]} fluidEvents
   * @returns {CaseEventBase[]}
   */
  (
    sedativesBeingUsed,
    positionsSkipped,
    airwayEnabled,
    vitalsTrackingEnabled,
    positionEvents,
    airwayEvents,
    fluidEvents
  ) => {
    /** @type {CaseEventBase[]} */
    const events = []

    if (vitalsTrackingEnabled) {
      events.push({
        id: 'caseStart',
        note: 'Started monitoring vitals',
        required: true,
      })
    } else {
      events.push({
        id: 'caseStart',
        note: 'Case Started',
        derived: true,
      })
    }

    if (sedativesBeingUsed) {
      events.push({
        id: 'anesthesiaStart',
        note: 'Anesthesia Time Started',
        maxTimeName: 'anesthesiaEnd',
        required: true,
      })
    }

    if (vitalsTrackingEnabled) {
      events.push({
        id: 'surgeryStart',
        note: 'Procedure Started',
        maxTimeName: 'surgeryEnd',
        showInEventReport: true,
        required: false,
      })
    }

    if (!positionsSkipped) {
      events.push(...positionEvents)
    }

    if (airwayEnabled) {
      events.push(...airwayEvents)
    }

    if (fluidEvents.length) {
      events.push(...fluidEvents)
    }

    if (sedativesBeingUsed) {
      events.push({
        id: 'anesthesiaEnd',
        note: 'Anesthesia Time Ended',
        minTimeName: 'caseStart',
        required: true,
      })
    }

    if (sedativesBeingUsed) {
      events.push({
        id: 'recoveryStart',
        note: 'Recovery started',
        preReq: 'caseStart',
        minTimeName: 'caseStart',
        maxTimeName: 'end',
        required: true,
      })
    }

    if (vitalsTrackingEnabled) {
      events.push({
        id: 'surgeryEnd',
        note: 'Procedure Ended',
        maxTimeName: 'end',
        preReq: 'surgeryStart',
        showInEventReport: true,
        required: false,
      })
    }

    if (vitalsTrackingEnabled) {
      events.push({
        id: 'vitalsEnd',
        note: 'Stopped monitoring vitals',
        preReq: sedativesBeingUsed ? 'recoveryStart' : 'caseStart',
        minTimeName: sedativesBeingUsed ? 'recoveryStart' : 'caseStart',
        maxTimeName: 'end',
        required: true,
      })
    } else {
      events.push({
        id: 'vitalsEnd',
        note: 'Case Complete',
        preReq: 'caseStart',
        minTimeName: 'caseStart',
        required: true,
      })
    }

    if (sedativesBeingUsed) {
      events.push({
        id: 'end',
        note: 'Recovery complete',
        preReq: 'vitalsEnd',
        minTimeName: 'vitalsEnd',
        required: true,
      })
    }

    return events
  }
)

/** @type {CaseSelector<CaseEventProcessed[]>} */
const selectBaseEventsProcessed = createSelector(
  selectBaseEvents,
  eventSelectors.selectEventTimesRaw,
  selectPositionTimesByEventId,
  selectAirwayTimesByEventId,
  selectFluidTimesByEventId,
  /**
   * @param {CaseEventBase[]} baseEvents
   * @param {CaseDataEvents} eventTimesRaw
   * @param {{ [key: string]: number }} positionTimesByEventId
   * @param {{ [key: string]: number }} airwayTimesByEventId
   * @returns {CaseEventProcessed[]}
   */
  (
    baseEvents,
    eventTimesRaw,
    positionTimesByEventId,
    airwayTimesByEventId,
    fluidTimes
  ) => {
    const times = {
      ...eventTimesRaw,
      ...positionTimesByEventId,
      ...airwayTimesByEventId,
      ...fluidTimes,
    }

    const eventsWithTimes = baseEvents.map(event => {
      const { id, ...rest } = event

      let time = times[id] || null

      return {
        id,
        ...rest,
        time,
        static: true,
      }
    })

    return eventsWithTimes.map(event => {
      const { id, required, ...rest } = event
      let isRequired = !!required

      const max = times[event.maxTimeName] || null
      const min = times[event.minTimeName] || null

      let canSet = true
      if (event.preReq) {
        canSet = !!times[event.preReq]
      }
      if (canSet && event.derived) {
        canSet = false
      }

      // surgery start is only required if surgery end is set
      if (id === 'surgeryEnd') {
        if (times.surgeryStart) {
          isRequired = true
        }
      }

      /** @type {CaseEventProcessed} */
      return {
        id,
        ...rest,
        required: isRequired,
        canSet,
        min,
        max,
        static: true,
      }
    })
  }
)

/** @type {CaseSelector<CaseEventProcessed[]>} */
export const selectAllEventsArray = createSelector(
  selectCustomEventsAsArray,
  selectBaseEventsProcessed,
  selectManualVitalsEvents,
  /**
   * @param {CustomCaseEvent[]} customEventsArray
   * @param {CaseEventProcessed[]} baseEventsArray
   * @param {ManualVitalsEvent[]} manualVitalEvents
   */
  (customEventsArray, baseEventsArray, manualVitalEvents) =>
    customEventsArray
      .concat(baseEventsArray)
      .concat(manualVitalEvents)
      .sort(({ time: timeA }, { time: timeB }) => {
        if (!timeA && timeB) {
          return 1
        }
        if (!timeB && timeA) {
          return -1
        }
        return timeA - timeB
      })
)

/** @type {CaseSelector<FinalEventsReport[]>} */
export const selectFinalEventsReport = createSelector(
  selectAllEventsArray,
  /** @param {CaseEventProcessed[]} events */
  events => {
    /** @type {FinalEventsReport[]} */
    const result = []
    let number = 0
    for (const event of events) {
      if (event.time && (!event.static || event.showInEventReport)) {
        number++
        result.push({
          id: event.id,
          time: event.time,
          note: event.note,
          // first one will be 1
          number,
        })
      }
    }
    return result
  }
)
